/**
 * Vorlage „Unterheben“.
 *
 * Auf dem Teig liegt luftiger Eischnee. Der Teigschaber sticht in der Mitte
 * ein, zieht am Boden entlang zur Seite und hebt den Teig über den Schnee.
 * Mit jeder Bewegung wird der Schnee weniger und die Masse heller.
 */
import { clamp, ease, fract, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { compact, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { poseAt } from '../parts/pose.ts';
import { arrow, counter } from '../parts/stage.ts';
import { place, spatula } from '../parts/utensils.ts';
import { bowl, contents } from '../parts/vessels.ts';
import { defineTemplate } from '../template.ts';

const BOWL = { cx: 112, rim: 80, width: 140, height: 54 } as const;
const LEVEL = 0.22;

/** Zeitplan. */
const TIME = { fold: [0.04, 0.84], reset: [0.9, 1] } as const;
/** Zahl der Hebebewegungen je Schleife. */
const FOLDS = 3;

/** Vorlage „Unterheben“. */
export const mixFoldTemplate = defineTemplate({
  id: 'mix.fold',
  group: 'mix',
  title: 'Unterheben',
  description:
    'Eischnee oder Sahne wird mit dem Teigschaber vorsichtig unter den Teig gehoben: in der Mitte einstechen, am Boden entlangziehen und den Teig darüberheben.',
  durationSeconds: 7,
  keywords: ['unterheben', 'unterziehen', 'vorsichtig unterheben', 'locker unterheben', 'teigschaber'],
  stillTime: 0.2,
  parameters: [],
  scene(_values, t) {
    const vessel = bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
    const surfaceY = BOWL.rim + vessel.depth * LEVEL;
    const rx = vessel.widthAt(LEVEL);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const folding = segment(t, ...TIME.fold);
    const mixed = ease.inOutSine(folding);

    // Masse: Teig mit Schneehaube und Schlieren; am Ende gleichmäßig hell.
    const mass = (amount: number): SceneNode => {
      const foam = 1 - amount;
      const mound = path()
        .move(BOWL.cx - 34 * foam - 6, surfaceY + 2)
        .curve(
          BOWL.cx - 30 * foam,
          surfaceY - 24 * foam,
          BOWL.cx + 26 * foam,
          surfaceY - 22 * foam,
          BOWL.cx + 32 * foam + 6,
          surfaceY + 2,
        )
        .quad(BOWL.cx, surfaceY + 10 * foam, BOWL.cx - 34 * foam - 6, surfaceY + 2)
        .close()
        .toString();
      const streaks: SceneNode[] = [-0.5, -0.1, 0.3].map((f, index) =>
        shape(
          path()
            .move(BOWL.cx + rx * f - 14, surfaceY + 4 - index * 3)
            .quad(BOWL.cx + rx * f, surfaceY - 6 + index * 2, BOWL.cx + rx * f + 16, surfaceY + 1)
            .toString(),
          { stroke: 'snow', strokeWidth: STROKE.outline },
        ),
      );
      return group([
        contents(vessel, LEVEL, 'shade', [
          faded(clamp(amount * 3) * (1 - clamp(amount * 1.6 - 0.6)), streaks),
        ]),
        faded(clamp(amount * 1.4 - 0.4), [contents(vessel, LEVEL, 'tint')]),
        foam > 0.12 ? shape(mound, styles.snow()) : null,
      ]);
    };

    // Bewegung des Schabers je Hebebewegung.
    const local = fract(folding * FOLDS);
    const active = folding > 0 && folding < 1;
    const pose = poseAt(local, [
      { at: 0, pose: { x: BOWL.cx + 8, y: surfaceY - 16, rotate: 8, lift: 0 } },
      { at: 0.2, pose: { x: BOWL.cx + 4, y: surfaceY + 14, rotate: 4, lift: 0 } },
      { at: 0.45, pose: { x: BOWL.cx - rx * 0.55, y: surfaceY + 10, rotate: -30, lift: 0 } },
      { at: 0.7, pose: { x: BOWL.cx - rx * 0.5, y: surfaceY - 16, rotate: -58, lift: 0 } },
      { at: 0.88, pose: { x: BOWL.cx - 12, y: surfaceY - 12, rotate: -24, lift: 0 } },
      { at: 1, pose: { x: BOWL.cx + 8, y: surfaceY - 16, rotate: 8, lift: 0 } },
    ]);
    const rest = { x: BOWL.cx + 8, y: surfaceY - 16, rotate: 8 };
    const tool = active ? pose : rest;
    return compact([
      ...counter(),
      ...vessel.back,
      reset > 0 ? group([mass(0)]) : null,
      faded(1 - reset, [mass(mixed)]),
      place(tool.x, tool.y, tool.rotate, spatula(local > 0.2 && local < 0.6 ? 0.4 : 0), 0.8),
      ...vessel.front,
    ]);
  },
  hints() {
    return [
      arrow([
        { x: BOWL.cx + 4, y: BOWL.rim - 26 },
        { x: BOWL.cx, y: BOWL.rim + 6 },
        { x: BOWL.cx - 44, y: BOWL.rim + 2 },
        { x: BOWL.cx - 52, y: BOWL.rim - 26 },
        { x: BOWL.cx - 22, y: BOWL.rim - 34 },
      ]),
    ];
  },
});
